import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, Sun, LogOut } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { clientNavItems } from '../config/clientNav';
import { MobileHeader } from '../components/client/mobile/MobileHeader';
import { MobileBottomNav } from '../components/client/mobile/MobileBottomNav';
import { MobileFlyout } from '../components/client/mobile/MobileFlyout';

interface ClientLayoutProps {
  children: React.ReactNode;
  currentView: string;
  onNavigate: (view: string) => void;
}

export const ClientLayout: React.FC<ClientLayoutProps> = ({ 
  children, 
  currentView, 
  onNavigate
}) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const isMobile = useIsMobile();
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('ctx-client');
    return () => document.documentElement.classList.remove('ctx-client');
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      // Redirect to client login page after successful logout
      navigate('/login/client', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, redirect to client login for security
      navigate('/login/client', { replace: true });
    }
  };

  const userName = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'Client User';
  const navigationItems = clientNavItems.filter((item) => item.enabled);

  if (isMobile) {
    return (
      <div className="mc-mobile-shell">
        <MobileHeader currentView={currentView} onMenuClick={() => setFlyoutOpen(true)} />
        <main className="main-content">{children}</main>
        <MobileBottomNav currentView={currentView} onNavigate={onNavigate} />
        <MobileFlyout
          isOpen={flyoutOpen}
          onClose={() => setFlyoutOpen(false)}
          currentView={currentView}
          onNavigate={onNavigate}
          userName={userName}
          theme={theme}
          onToggleTheme={toggleTheme}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  return (
    <div className="app-layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="app-title">Mission Control</h1>
          <span className="app-subtitle">Health Portal</span>
        </div>

        <nav className="sidebar-nav">
          {/* Client Navigation */}
          {navigationItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.view}
                onClick={() => onNavigate(item.view)}
                className={`nav-item ${currentView === item.view ? 'active' : ''}`}
              >
                <IconComponent size={20} />
                <span className="nav-label">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleTheme}>
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
            <span>{theme === 'light' ? 'Dark' : 'Light'} Mode</span>
          </button>

          <div className="user-info">
            <div className="user-details">
              <span className="user-name">{user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : 'Client User'}</span>
              <span className="user-role">CLIENT</span>
            </div>
            <button className="logout-btn" onClick={handleLogout}>
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {children}
      </main>

      <style>{`
        .app-layout {
          display: flex;
          height: 100vh;
          background: var(--bg-primary);
        }

        .sidebar {
          width: 280px;
          background: var(--bg-primary);
          border-right: 1px solid var(--border-secondary);
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }

        .sidebar-header {
          padding: 24px 20px;
          border-bottom: 1px solid var(--border-secondary);
        }

        .app-title {
          font-size: 20px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }

        .app-subtitle {
          font-size: 14px;
          color: var(--accent-blue);
          font-weight: 500;
        }

        .sidebar-nav {
          flex: 1;
          padding: 20px 12px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: var(--radius-medium);
          color: var(--text-secondary);
          text-decoration: none;
          font-weight: 500;
          transition: all 0.2s ease;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          cursor: pointer;
        }

        .nav-item:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .nav-item.active {
          background: var(--accent-blue);
          color: white;
        }

        .nav-label {
          font-size: 14px;
        }

        .sidebar-footer {
          padding: 20px;
          border-top: 1px solid var(--border-secondary);
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .theme-toggle {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border: none;
          border-radius: var(--radius-medium);
          color: var(--text-secondary);
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .theme-toggle:hover {
          background: var(--border-secondary);
          color: var(--text-primary);
        }

        .user-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border-radius: var(--radius-medium);
        }

        .user-details {
          display: flex;
          flex-direction: column;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .user-role {
          font-size: 12px;
          color: var(--accent-blue);
          font-weight: 500;
        }

        .logout-btn {
          background: none;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .logout-btn:hover {
          color: var(--accent-blue);
          background: var(--bg-primary);
        }

        .main-content {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          background: var(--bg-primary);
        }
      `}</style>
    </div>
  );
};